import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { omit } from 'underscore'
import { Button, IconButton } from '@mes-amis/galette-design-system'
import { DocumentEditor, FormDataStoreProvider, PrefillBanner, ExternalErrorsBanner, ErrorReporterProvider, ConfirmDialog, isBlank } from '@mes-amis/super-form'
import '@mes-amis/super-form/styles.css'
import './style.scss'
import createChannel from '../../../../cables/cable'
import { updateUrlParams } from '../../../../helpers/url_helper'
import { loadTurboFrame } from '../../../../helpers/turbo_frames'

// Compare two HLC strings of the form `<physical-ms>-<logical>-<actor>`.
// Returns true iff `incoming` represents a strictly newer write than `current`.
// Falsy `incoming` is never newer; missing `current` is always older. For
// malformed timestamps (e.g. legacy MD5 content_revision values from documents
// that pre-date CRDT metadata), fall back to inequality so we still accept
// updates that look different — better to over-apply than to silently drop a
// real broadcast.
const isFieldHLCNewer = (incoming, current) => {
  if (!incoming) return false
  if (!current) return true
  const parse = (rev) => {
    const m = /^(\d+)-(\d+)-(.+)$/.exec(rev)
    return m ? { physical: Number(m[1]), logical: Number(m[2]), actor: m[3] } : null
  }
  const a = parse(incoming)
  const b = parse(current)
  if (!a || !b) return incoming !== current
  if (a.physical !== b.physical) return a.physical > b.physical
  if (a.logical !== b.logical) return a.logical > b.logical
  return a.actor > b.actor
}

// Largest physical-ms component across a meta object's HLC stamps. Feeds the
// editor's HLC clock so a stamp it mints can never sort below a stamp it has
// already seen — a machine with a slow clock would otherwise mint stamps that
// lose the server-side CRDT merge to the very values they are replacing.
const maxHlcPhysical = (meta) => {
  let max = 0
  for (const stamp of Object.values(meta || {})) {
    const m = /^(\d+)-/.exec(stamp)
    if (m) max = Math.max(max, Number(m[1]))
  }
  return max
}

// Forward super-form's internal, non-throwing error reports to Honeybadger.
// super-form surfaces unexpected-but-recoverable conditions through this
// channel instead of crashing the render tree (e.g. a form definition
// referencing a specialized field type it has no renderer for). The browser
// Honeybadger SDK is loaded globally (layouts/_honeybadger) and is absent in
// development/test, so fall back to console.error when it isn't present.
// Module-level so the reference is stable (ErrorReporterProvider memoizes on it).
const reportSuperFormError = (report) => {
  if (typeof window !== 'undefined' && window.Honeybadger) {
    window.Honeybadger.notify(report.error || report.message, {
      name: 'SuperFormError',
      message: report.message,
      context: report.context,
    })
  } else {
    console.error('[ReactDocumentEditor] super-form error:', report.message, report.context || {}, report.error)
  }
}

// Prefer the live meta tag (rotates with the session) over the server-rendered prop.
const getCsrfToken = (fallback) => window.document.querySelector('meta[name="csrf-token"]')?.content || fallback

// Walk the form definition and collect the slugs of `disabled` or `virtual`
// fields: core-data mirrors (person.first_name, person.date_of_birth, …) and
// derived values (person.computed_age). The client never edits these, so we
// (a) keep them out of autosave PATCHes — the server owns them — and (b) apply
// them from a broadcast without a per-field HLC. Core-data fields carry no HLC
// in the broadcast (super_form_meta only tracks CRDT-written answers, never the
// server-derived Person mirrors), so the normal staleness guard would otherwise
// drop a server-changed birthdate — and then the `age` formula would have no new
// input to recompute from.
const collectDerivedDisplaySlugs = (form) => {
  const slugs = new Set()
  const visit = (config) => {
    if (!config || typeof config !== 'object') return
    for (const [name, field] of Object.entries(config)) {
      if (!field || typeof field !== 'object') continue
      if (field.virtual === true || field.disabled === true) slugs.add(name)
      if (field.fields) visit(field.fields)
      if (field.summaries) visit(field.summaries)
      if (field.rows) {
        for (const row of Object.values(field.rows)) {
          if (row && row.fields) visit(row.fields)
        }
      }
    }
  }
  visit(form)
  return slugs
}

// Map a contact JSON row (from the online contacts endpoint, which reuses the
// offline jbuilder) to a super-form ModelRecord. Names nest under `person`
// (mirrors ContactViewerField.formatName); fall back to a stable id label.
const mapContactRecord = (c) => ({
  id: c.id,
  label: c.display_name || [c.person?.first_name, c.person?.last_name].filter(Boolean).join(' ') || `Contact #${c.id}`,
  data: c,
})

// The editor saves over fetch, so it emits the same `autosave:*` events the
// Stimulus autosave controllers do. `#test-autosave` (layouts/_test_wait_for)
// counts them, which is what lets system specs wait for a save to land instead
// of polling. Always pair these in a finally: an unbalanced `started` leaves the
// harness stuck in-progress.
const autosaveStarted = () => document.dispatchEvent(new CustomEvent('autosave:started'))
const autosaveCompleted = () => document.dispatchEvent(new CustomEvent('autosave:completed'))

const ReactDocumentEditor = (props) => {
  const {
    template, document: doc, client, urls, csrfToken, signedStreamNames, readonly, fieldPermissions,
    title, clientName, clientSlug, defaultPanelOpen, initialTab: requestedTab,
    dateCompleted, reopened, minorChange, hasSignatureConfig, helpDocument, summaryHelpDocument,
    fieldPrefills, unsavedPrefillSlugs = [], hiddenPrefillValues = {}, confirmAllEnabled, signatures: initialSignatures, currentSignerName, timezone, scannedDocument,
    backLink, canReopen, showAlgorithmExplorer, allowAllAlgorithms
  } = props

  // Form data lives in the FormDataStoreProvider mounted below. The host
  // keeps three parallel refs alongside it:
  //   - initialFormData: seed for the provider on mount (frozen at first render)
  //   - latestFormDataRef: mirror of every field write, used by the autosave
  //     to compute changed fields without subscribing to the store
  //   - lastSavedDataRef: snapshot of what the server has acknowledged, used
  //     to skip ActionCable echoes and detect local unsaved changes
  // actionsRef receives setFormData/mergeFormData from the provider so the
  // broadcast handler can push server-side changes into the store without
  // round-tripping through React state.
  const initialFormData = useRef(doc?.super_form || {}).current
  const latestFormDataRef = useRef(doc?.super_form || {})
  // Prefills the server computed for this render (e.g. `prefill: current_time`)
  // but holds no column value for are not "saved": leaving them out of the
  // snapshot makes the first save transmit them, so a reopen shows the same
  // answer instead of a re-run prefill. Saves are still triggered
  // only by user edits, blur, or Save & Exit — never by opening the editor.
  const lastSavedDataRef = useRef(omit(doc?.super_form || {}, unsavedPrefillSlugs))
  // Echo-skip revision tracked per signed stream name (main + permission streams).
  const lastRevisionRef = useRef({})
  // Per-field HLC the client has accepted (from initial render, save responses,
  // or applied broadcasts). The broadcast handler uses this to skip a field
  // whose incoming HLC is older than what we've already accepted — protecting
  // against late-arriving broadcasts whose overall revision is "newer" but
  // whose per-field data is stale relative to a more recent local save.
  const fieldMetaRef = useRef({ ...(doc?.super_form_meta || {}) })
  const actionsRef = useRef(null)
  const saveTimeoutRef = useRef(null)
  // Preserved answers a depends_on edit re-revealed (slug → value), queued by
  // onFieldsRevealed until the next successful autosave transmits them with a
  // fresh HLC stamp.
  const pendingRevealedRef = useRef({})
  const dateCompletedRef = useRef(dateCompleted || '')
  const hlcCounterRef = useRef(0)
  // HLC physical clock: the newest physical-ms seen across any stamp (initial
  // meta, broadcasts) or minted here. buildHlc clamps strictly above it.
  const hlcClockRef = useRef(maxHlcPhysical(doc?.super_form_meta))
  const [superFormMeta, setSuperFormMeta] = useState(doc?.super_form_meta || {})
  // Pending prefills whose answer a depends_on hide just wiped; confirmed by an
  // effect below, once confirmPrefills is in scope.
  const [wipedPrefillSlugs, setWipedPrefillSlugs] = useState([])
  const [errors, setErrors] = useState({})
  const [externalErrors, setExternalErrors] = useState([])
  // Signature slots update in place as each one is signed; the document status
  // itself only changes through the API + a page load (finalize reloads, the
  // last signature redirects), so it's read straight from the server props.
  const [signatures, setSignatures] = useState(initialSignatures || [])
  const [showNoChangesNotice, setShowNoChangesNotice] = useState(false)
  const [revertError, setRevertError] = useState(null)
  // Set when a broadcast or a 409 reveals the document was finished elsewhere
  // while this editor was open. The ref stops further autosaves immediately
  // (state alone is too slow for the debounced save); the state renders the
  // explain-and-reload dialog.
  const [remoteLockNotice, setRemoteLockNotice] = useState(false)
  const remotelyLockedRef = useRef(false)
  // Suppresses the remote-lock dialog for our own finalize: its status
  // broadcast echoes back to this tab before the redirect/reload lands.
  const finishingRef = useRef(false)
  const signingActive = !!hasSignatureConfig && ['waiting_for_signature', 'signing_in_progress'].includes(doc?.status)
  // Deep link from the documents list signature links (?tab=signature) and the
  // summary route (which asks for the Summary tab); a doc awaiting signatures
  // opens on the Signature tab regardless.
  const initialTab = signingActive ? 'signature' : requestedTab

  // Disabled/virtual slugs the client only displays (core-data mirrors +
  // person.computed_age). Excluded from autosave and applied from broadcasts
  // without a per-field HLC — see collectDerivedDisplaySlugs.
  const derivedDisplaySlugs = useMemo(
    () => collectDerivedDisplaySlugs(template?.super_form?.definition?.form || {}),
    [template]
  )

  // Mint one HLC stamp — `${ms}-${counter}-${actor}`, the same wire format as
  // the offline app. Every value this editor transmits as changed must carry
  // one: the server-side CRDT merge resolves an unstamped changed field
  // against any stamp the column already holds, and the column wins — so an
  // unstamped re-edit of a previously saved answer is silently reverted. The
  // physical part clamps strictly above the clock (stamps already seen), so a
  // slow client clock can't mint a stamp that loses to this editor's own
  // earlier saves.
  const buildHlc = useCallback(() => {
    const ms = Math.max(Date.now(), hlcClockRef.current + 1)
    hlcClockRef.current = ms
    const counter = hlcCounterRef.current++
    return `${ms}-${counter}-admin-${doc?.id ?? 'session'}`
  }, [doc?.id])

  const performSave = useCallback(async () => {
    // A locked document (completed / awaiting signatures) never autosaves —
    // stray client-side writes would be rejected by the server anyway.
    if (readonly || remotelyLockedRef.current) return

    const data = latestFormDataRef.current
    const changedFields = {}
    for (const [key, value] of Object.entries(data)) {
      // Never write a field the user can't view — the server rejects these too,
      // but excluding them here avoids a pointless round-trip and a masked
      // placeholder ('****') ever being sent as a value.
      if (fieldPermissions && fieldPermissions[key] === false) continue
      // Display-only fields (virtual/disabled) are the server's to write, even
      // when it lists one as an unsaved prefill — a disabled input is never submitted.
      if (derivedDisplaySlugs.has(key)) continue
      if (JSON.stringify(value) !== JSON.stringify(lastSavedDataRef.current[key])) {
        changedFields[key] = value
      }
    }

    // Fields a depends_on edit re-revealed carry their preserved (unchanged)
    // value plus a fresh stamp, so the answer the user can now see competes in
    // CRDT merges as of the gating edit, not as of when it was typed. A field
    // the user re-edited after the reveal is already in changedFields with the
    // newer value — the reveal entry only fills the unchanged case.
    const revealedKeys = Object.keys(pendingRevealedRef.current)
    for (const key of revealedKeys) {
      if (key in changedFields) continue
      if (fieldPermissions && fieldPermissions[key] === false) continue
      const value = key in data ? data[key] : pendingRevealedRef.current[key]
      // Blank policy (host-owned — only our CRDT meta can tell the two blanks
      // apart): a blank with no per-field HLC stamp was never answered, so
      // there is nothing to re-assert. A stamped blank is a deliberate
      // clear/uncheck the user is looking at — it competes like any answer.
      if (isBlank(value) && !fieldMetaRef.current[key]) continue
      // undefined isn't JSON — it would drop out of the payload while its key
      // still got stamped. A stamped blank re-asserts as an explicit null.
      changedFields[key] = value === undefined ? null : value
    }
    if (Object.keys(changedFields).length === 0) return

    // Stamp exactly the fields we transmit — never the untouched rest of the
    // form, whose fresh stamps would clobber an offline device's per-field
    // HLCs the way a blanket full-form stamp would.
    const metaPatch = {}
    for (const key of Object.keys(changedFields)) metaPatch[key] = buildHlc()

    autosaveStarted()
    try {
      const token = getCsrfToken(csrfToken)

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { super_form: changedFields, super_form_meta: metaPatch } })
      })

      if (response.ok || response.status === 204) {
        lastSavedDataRef.current = { ...lastSavedDataRef.current, ...changedFields }
        // Only the reveals this save carried — ones queued mid-flight stay
        // pending for the next save.
        for (const key of revealedKeys) delete pendingRevealedRef.current[key]

        // Advance lastRevisionRef + per-field HLC from the save response so any
        // broadcast carrying stale values for the fields we just wrote (an
        // echo of an earlier render that hadn't seen this save yet) gets
        // skipped. Without this, lastRevisionRef only moves when a broadcast
        // lands, and field-level meta never tracks the local writes at all.
        try {
          const body = await response.json()
          if (body?.revision) {
            // Our save fans out the same revision to every stream we're on, so
            // mark it acknowledged on all of them to skip the echoes.
            const next = { ...lastRevisionRef.current }
            for (const name of Object.values(signedStreamNames || {})) {
              if (name) next[name] = body.revision
            }
            lastRevisionRef.current = next
          }
        } catch (_) {
          // Empty body (204 from older servers) — nothing to advance.
        }
        // Track the real per-field HLCs we minted (not the whole-doc revision)
        // so a broadcast echoing these fields compares against the true stamp.
        const nextMeta = { ...fieldMetaRef.current }
        for (const key of Object.keys(changedFields)) nextMeta[key] = metaPatch[key]
        fieldMetaRef.current = nextMeta
      }

      if (!response.ok && response.status === 401) {
        window.location.reload()
      }

      // The server rejects writes to a locked document (finished elsewhere
      // while this editor was open). Stop saving and explain — every retry
      // would fail the same way.
      if (!response.ok && response.status === 409) {
        const body = await response.json().catch(() => ({}))
        if (body?.error === 'document_completed' && !finishingRef.current && !remotelyLockedRef.current) {
          remotelyLockedRef.current = true
          setRemoteLockNotice(true)
        }
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Autosave failed:', error)
    } finally {
      autosaveCompleted()
    }
  }, [urls.autosave, csrfToken, fieldPermissions, signedStreamNames, readonly, buildHlc, derivedDisplaySlugs])

  const scheduleSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => performSave(), 900)
  }, [performSave])

  const handleFieldChange = useCallback((field, value, meta) => {
    // Display-only fields (virtual/disabled, e.g. person.computed_age) are owned
    // by the server; never autosave them. The store already holds the value for
    // display, and the client recomputes derived ones from their inputs.
    if (derivedDisplaySlugs.has(field)) return
    const previous = latestFormDataRef.current[field]
    latestFormDataRef.current = { ...latestFormDataRef.current, [field]: value }
    // The package clears a gate's direct dependents when the gate closes. A pending
    // prefill wiped that way is consumed — the answer changed — so it gets confirmed
    // and nothing stays pending or is re-applied on the next reveal.
    if (isBlank(value) && !isBlank(previous) && fieldPrefills?.[field]?.requires_confirmation &&
        !superFormMeta[`confirmed_prefill:${field}`]) {
      setWipedPrefillSlugs((prev) => (prev.includes(field) ? prev : [...prev, field]))
    }
    // A server-rejected finalize flagged this field; the user is addressing it
    // now, so retire the stale error — the next finalize re-checks everything.
    setErrors((prev) => {
      if (!(field in prev)) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
    // Engine-driven writes (mount backfill, formula cascades) are mirrored into
    // the ref so they ride along with the next user save, but must not schedule
    // one themselves — otherwise merely opening the editor PATCHes the document.
    if (meta?.source === 'engine') return
    scheduleSave()
  }, [scheduleSave, derivedDisplaySlugs, fieldPrefills, superFormMeta])

  // A depends_on edit re-revealed fields whose preserved answers the user can
  // now see (super-form reports editor-originated reveals, blanks included —
  // the blank policy is ours, applied at save time against our HLC meta).
  // Queue them so the next autosave transmits value + fresh HLC — the
  // re-displayed answer competes in CRDT merges as of the gating edit.
  //
  // A revealed field the server could not prefill at render because it was
  // hidden then (hiddenPrefillValues) gets its prefill applied here, the way
  // the Archiver would on a reload: only into a blank field whose prefill was
  // never confirmed, so a deliberate clear stays cleared and a stored answer is
  // never overwritten. The applied value rides along with the queued reveal.
  const handleFieldsRevealed = useCallback((fields) => {
    let queued = false
    const prefillPatch = {}
    for (const [field, value] of Object.entries(fields)) {
      if (derivedDisplaySlugs.has(field)) continue
      const prefill = hiddenPrefillValues[field]
      const applyPrefill = prefill !== undefined && isBlank(value) &&
        !superFormMeta[`confirmed_prefill:${field}`] && !(fieldPermissions && fieldPermissions[field] === false)
      if (applyPrefill) prefillPatch[field] = prefill
      pendingRevealedRef.current[field] = applyPrefill ? prefill : value
      queued = true
    }
    if (Object.keys(prefillPatch).length > 0) {
      latestFormDataRef.current = { ...latestFormDataRef.current, ...prefillPatch }
      actionsRef.current?.mergeFormData(prefillPatch)
    }
    if (queued) scheduleSave()
  }, [scheduleSave, derivedDisplaySlugs, hiddenPrefillValues, superFormMeta, fieldPermissions])

  // Tell the test harness the editor is mounted and its saves are observable,
  // matching `autosave:ready` from the Stimulus autosave controllers.
  useEffect(() => { document.dispatchEvent(new CustomEvent('autosave:ready')) }, [])

  // Field.tsx buffers text/number input and only propagates on blur, so
  // trigger one when the page is hidden (tab switch, navigation, etc.).
  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden') document.activeElement?.blur?.()
    }
    document.addEventListener('visibilitychange', flush)
    return () => document.removeEventListener('visibilitychange', flush)
  }, [])

  // Sensitive fields broadcast on their own per-permission streams, so the
  // editor subscribes to every signed name the server hands it (main + any of
  // ssn/medicare/medicaid the user may view). The handler is identical per
  // stream; only the echo-skip revision is tracked per stream name, because a
  // single save fans out the SAME revision to main + permission streams and a
  // shared revision ref would drop the second stream's payload for a remote save.
  const streamNamesKey = JSON.stringify(signedStreamNames || {})
  useEffect(() => {
    const names = Object.values(signedStreamNames || {}).filter(Boolean)
    if (names.length === 0) return

    const makeReceived = (streamName) => (data) => {
      try {
        const message = typeof data === 'string' ? JSON.parse(data) : data
        if (message?.type !== 'document_update' || !message.document) return

        const serverDoc = message.document

        // The document was finished somewhere else while this editor is open.
        // `locked` is the server's own Document#locked? — no client-side list
        // of locked statuses to keep in sync. Stop autosaving and tell the
        // user why their edits stopped taking effect. Our own finalize also
        // echoes here — finishingRef suppresses that case.
        if (serverDoc.locked === true &&
            !readonly && !finishingRef.current && !remotelyLockedRef.current) {
          remotelyLockedRef.current = true
          setRemoteLockNotice(true)
        }

        const serverRevision = serverDoc.revision || null

        // Skip echoes of our own saves (per stream — see comment above).
        if (serverRevision && serverRevision === lastRevisionRef.current[streamName]) return

        const serverFormData = serverDoc.super_form
        if (!serverFormData || typeof serverFormData !== 'object') return

        const serverFieldMeta = (serverDoc.super_form_meta && typeof serverDoc.super_form_meta === 'object')
          ? serverDoc.super_form_meta
          : {}

        // Keep the HLC clock ahead of every stamp another writer has produced,
        // so this editor's next stamp outranks them.
        hlcClockRef.current = Math.max(hlcClockRef.current, maxHlcPhysical(serverFieldMeta))

        const patch = {}
        for (const [key, value] of Object.entries(serverFormData)) {
          const localUnsaved = JSON.stringify(latestFormDataRef.current[key]) !== JSON.stringify(lastSavedDataRef.current[key])
          if (localUnsaved) continue

          // Per-field HLC guard: if our locally-tracked HLC for this field
          // is already >= the broadcast's HLC for it, the broadcast carries
          // stale data for that field — skip. This is the key protection
          // against late broadcasts whose document revision moved forward
          // (because some other field was edited) but whose super_form
          // payload for this field reflects a pre-save snapshot.
          //
          // Core-data display fields (disabled/virtual, e.g. person.date_of_birth
          // and person.computed_age) are exempt from that guard: they carry no
          // per-field HLC in the broadcast (super_form_meta only tracks
          // CRDT-written answers, never the server-derived Person mirrors), so the
          // guard would drop them every time. In particular a birthdate changed on
          // the Client Profile arrives with no HLC — without this exemption it is
          // dropped and the `age` formula never fires, leaving Age stale.
          if (!derivedDisplaySlugs.has(key) && !isFieldHLCNewer(serverFieldMeta[key], fieldMetaRef.current[key])) continue

          patch[key] = value
          latestFormDataRef.current = { ...latestFormDataRef.current, [key]: value }
          fieldMetaRef.current = { ...fieldMetaRef.current, [key]: serverFieldMeta[key] || fieldMetaRef.current[key] }
        }
        lastRevisionRef.current = { ...lastRevisionRef.current, [streamName]: serverRevision }
        // Only fields we actually accepted should advance lastSavedDataRef;
        // blindly merging serverFormData (the pre-fix behavior) corrupts
        // the "what the server has acknowledged" snapshot when we skipped
        // some keys for staleness.
        lastSavedDataRef.current = { ...lastSavedDataRef.current, ...patch }

        if (Object.keys(patch).length > 0) {
          actionsRef.current?.mergeFormData(patch)
        }

        // PARC state from the broadcast — pending set drops naturally when
        // `confirmed_prefill:<slug>` keys arrive in super_form_meta from
        // another tab.
        if (serverDoc.super_form_meta && typeof serverDoc.super_form_meta === 'object') {
          setSuperFormMeta((current) => ({ ...current, ...serverDoc.super_form_meta }))
        }
      } catch (error) {
        console.error('[ReactDocumentEditor] Failed to process broadcast:', error)
      }
    }

    const subscriptions = names.map((signedStreamName) => createChannel(
      { channel: 'Turbo::StreamsChannel', signed_stream_name: signedStreamName },
      { received: makeReceived(signedStreamName) }
    ))

    return () => {
      subscriptions.forEach((subscription) => subscription && subscription.unsubscribe())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamNamesKey])

  const handleBlur = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    performSave()
  }, [performSave])

  const saveDateCompleted = useCallback(async (value) => {
    autosaveStarted()
    try {
      const token = getCsrfToken(csrfToken)

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { recorded_at: value || null } })
      })

      if (!response.ok && response.status === 401) {
        window.location.reload()
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Date completed save failed:', error)
    } finally {
      autosaveCompleted()
    }
  }, [urls.autosave, csrfToken])

  const handleFinish = useCallback(async (validatedData) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    setExternalErrors([])
    finishingRef.current = true

    // Stamp every field whose value the server hasn't acknowledged yet — the
    // debounced save we just cancelled, or a text field that never blurred
    // (typing the last answer and clicking Finish lands here). Finalize
    // submits the whole form against the page-load meta, so without a fresh
    // stamp a re-edited field's earlier stamp wins the server's CRDT merge
    // and the user's final answer is silently reverted — then flagged as an
    // unanswered required question.
    const tailMeta = {}
    for (const [key, value] of Object.entries(validatedData || {})) {
      if (derivedDisplaySlugs.has(key)) continue
      if (fieldPermissions && fieldPermissions[key] === false) continue
      if (JSON.stringify(value) !== JSON.stringify(lastSavedDataRef.current[key])) {
        tailMeta[key] = buildHlc()
      }
    }

    try {
      const token = getCsrfToken(csrfToken)

      // Force JSON content-negotiation.
      // Include the current `super_form_meta` so the server's CRDT merge sees
      // any PARC confirms that were still optimistic locally
      const response = await fetch(urls.finalize, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          document: {
            super_form: validatedData,
            super_form_meta: { ...superFormMeta, ...tailMeta },
          }
        })
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok) {
        if (data.signing_stage) {
          // Signature-config template: reload the editor. The server now renders
          // it locked with the signature slots, opening on the Signature tab.
          window.location.reload()
          return
        }
        window.location.href = data.redirect_url || urls.documents
        return
      }

      // Failed finalize: this tab is staying on the page, so re-arm the
      // remote-lock detection (success paths navigate away and never get here).
      finishingRef.current = false

      if (data.error === 'no_significant_changes') {
        setShowNoChangesNotice(true)
        return
      }

      if (data.field_errors && typeof data.field_errors === 'object') {
        setErrors(data.field_errors)
      }
      if (Array.isArray(data.external_errors)) {
        setExternalErrors(data.external_errors)
      }
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        console.warn(`[ReactDocumentEditor] Could not finish the document:\n\n${data.errors.join('\n')}`)
      } else if (!(Array.isArray(data.external_errors) && data.external_errors.length > 0)) {
        console.error('[ReactDocumentEditor] Could not finish the document. Please reload the page and try again.')
      }
    } catch (error) {
      finishingRef.current = false
      console.error('[ReactDocumentEditor] Finalize failed:', error)
    }
  }, [urls.finalize, urls.documents, csrfToken, superFormMeta, derivedDisplaySlugs, fieldPermissions, buildHlc])

  // Complete one signature slot. Returns `{ ok, error? }` for the package's
  // SignatureTab to display inline. Navigates to the Documents list once the
  // last required signature completes the document.
  // `signOptions` arrives from the ClientProxy slot's signer identity question:
  // the representative choice, the signatory it resolves to (client or legal
  // rep — omitted when the current user signs on behalf), and the optional
  // signing statement. The controller already permits all three.
  const handleSign = useCallback(async (signatureId, name, signOptions) => {
    const slot = signatures.find((s) => s.id === signatureId)
    if (!slot?.sign_url) return { ok: false, error: 'Could not complete the signature. Please reload the page.' }

    try {
      const token = getCsrfToken(csrfToken)

      const signingSignature = { name_confirmation: name, signed_at: new Date().toISOString() }
      if (signOptions?.client_signature_representative) {
        signingSignature.client_signature_representative = signOptions.client_signature_representative
        if (signOptions.signatory_id) signingSignature.signatory_id = signOptions.signatory_id
        if (signOptions.signing_statement) signingSignature.signing_statement = signOptions.signing_statement
      }

      const response = await fetch(slot.sign_url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ signing_signature: signingSignature })
      })

      if (response.status === 401) {
        window.location.reload()
        return { ok: false }
      }

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        const error = Array.isArray(data.errors) && data.errors.length > 0
          ? data.errors.join('. ')
          : 'Could not complete the signature. Please try again.'
        return { ok: false, error }
      }

      if (data.redirect_url) {
        window.location.href = data.redirect_url
        return { ok: true }
      }

      setSignatures((prev) => prev.map((s) => (
        s.id === signatureId ? { ...s, ...(data.signature || { status: 'completed', name_confirmation: name }), sign_url: s.sign_url } : s
      )))
      return { ok: true }
    } catch (error) {
      console.error('[ReactDocumentEditor] Sign failed:', error)
      return { ok: false, error: 'Could not complete the signature. Please try again.' }
    }
  }, [signatures, csrfToken])

  // Unlock the form during the signing stage. Reopening invalidates the
  // signing request, so any signature progress is lost — the package's
  // DocumentEditor shows a confirm dialog before calling this.
  const handleReopen = useCallback(async () => {
    try {
      const token = getCsrfToken(csrfToken)

      const response = await fetch(urls.reopen, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        }
      })

      if (response.ok || response.status === 401) {
        // Reload the edit page (keeps the react_super_form query param) — the
        // server now renders the document as re-opened and editable.
        window.location.reload()
        return
      }

      const data = await response.json().catch(() => ({}))
      console.error('[ReactDocumentEditor] Reopen failed:', data.error || response.status)
    } catch (error) {
      console.error('[ReactDocumentEditor] Reopen failed:', error)
    }
  }, [urls.reopen, csrfToken])

  // Undo the re-open: restore the previous edition's values and re-validate its
  // signing request, so signatures collected before the re-open become valid
  // again (see Document#revert!). Offered from the no-changes dialog.
  const handleRevert = useCallback(async () => {
    try {
      const token = getCsrfToken(csrfToken)

      const response = await fetch(urls.revert, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        }
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok) {
        window.location.href = data.redirect_url || urls.documents
        return
      }

      setShowNoChangesNotice(false)
      setRevertError(data.error || 'Unable to revert the document. Please reload the page and try again.')
    } catch (error) {
      console.error('[ReactDocumentEditor] Revert failed:', error)
      setShowNoChangesNotice(false)
      setRevertError('Unable to revert the document. Please reload the page and try again.')
    }
  }, [urls.revert, urls.documents, csrfToken])

  const handleSaveAndExit = useCallback(async () => {
    // Flush any typed-but-not-yet-blurred value before performSave reads the diff.
    document.activeElement?.blur?.()
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    await performSave()
    window.location.href = urls.documents
  }, [performSave, urls.documents])

  const handleBack = useCallback(() => {
    window.location.href = urls.documents
  }, [urls.documents])

  // Persist the notes/help panel's open state in the URL (`?sidebar=open`) so a
  // refresh keeps the panel as the user left it (GHIB-387). Mirrors the classic
  // editor's server-rendered sidebar, which reads `params[:sidebar]`.
  const handlePanelOpenChange = useCallback((open) => {
    updateUrlParams({ sidebar: open ? 'open' : null })
  }, [])

  // PARC confirm path. Each confirm writes a `confirmed_prefill:<slug>`
  // HLC timestamp into `super_form_meta`. The server-side Crdt::Update
  // merges these per-key (server is authoritative), so concurrent confirms
  // from another admin tab converge cleanly via the cable broadcast.

  // PARC confirm path. A confirm must persist BOTH the confirmation marker AND
  // the accepted prefill value in the same request. The value lives in the React form
  // store (seeded from the initial render, where Archiver runs
  // prepare_for_rendering) but was never written to a column
  const confirmPrefills = useCallback(async (slugs) => {
    if (!Array.isArray(slugs) || slugs.length === 0) return

    const superFormPatch = {}
    const metaPatch = {}
    for (const slug of slugs) {
      // Carry the accepted value (+ its own value HLC) only when the field holds
      // one in the form store and the viewer is allowed to write it.
      if (slug in latestFormDataRef.current && !(fieldPermissions && fieldPermissions[slug] === false)) {
        superFormPatch[slug] = latestFormDataRef.current[slug]
        metaPatch[slug] = buildHlc()
      }
      metaPatch[`confirmed_prefill:${slug}`] = buildHlc()
    }

    // Optimistic local confirm state so the tint/dot clears immediately.
    setSuperFormMeta((prev) => ({ ...prev, ...metaPatch }))
    const rollbackMarkers = () => {
      setSuperFormMeta((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(metaPatch)) delete next[key]
        return next
      })
    }

    const hasValues = Object.keys(superFormPatch).length > 0
    const body = hasValues
      ? { document: { super_form: superFormPatch, super_form_meta: metaPatch } }
      : { document: { super_form_meta: metaPatch } }

    autosaveStarted()
    try {
      const token = getCsrfToken(csrfToken)

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      })

      if (response.status === 401) {
        window.location.reload()
        return
      }
      if (!response.ok) {
        console.warn('[ReactDocumentEditor] Prefill confirm not persisted (status', response.status, ')')
        rollbackMarkers()
        return
      }

      if (hasValues) {
        // The values are now server-acknowledged. Mirror performSave's
        // bookkeeping so a broadcast echoing these fields back isn't mistaken
        // for a remote edit: mark them saved, track the value HLC we sent, and
        // advance the per-stream revision from the response.
        lastSavedDataRef.current = { ...lastSavedDataRef.current, ...superFormPatch }
        const nextFieldMeta = { ...fieldMetaRef.current }
        for (const slug of Object.keys(superFormPatch)) nextFieldMeta[slug] = metaPatch[slug]
        fieldMetaRef.current = nextFieldMeta
        try {
          const respBody = await response.json()
          if (respBody?.revision) {
            const next = { ...lastRevisionRef.current }
            for (const name of Object.values(signedStreamNames || {})) {
              if (name) next[name] = respBody.revision
            }
            lastRevisionRef.current = next
          }
        } catch (_) {
          // Empty body — nothing to advance.
        }
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Prefill confirm failed:', error)
      rollbackMarkers()
    } finally {
      autosaveCompleted()
    }
  }, [urls.autosave, csrfToken, fieldPermissions, signedStreamNames, buildHlc])

  // Confirm the prefills a hide wiped (see handleFieldChange). Runs here rather
  // than in the change handler because confirmPrefills is declared after it.
  useEffect(() => {
    if (wipedPrefillSlugs.length === 0) return
    setWipedPrefillSlugs([])
    void confirmPrefills(wipedPrefillSlugs)
  }, [wipedPrefillSlugs, confirmPrefills])

  // Single-slug adapter for the dot/focus confirm. The batch prop
  // (onPrefillsConfirmed) wires straight to confirmPrefills, which already
  // guards a non-array / empty list.
  const handlePrefillConfirmed = useCallback((slug) => {
    if (!slug) return
    void confirmPrefills([slug])
  }, [confirmPrefills])

  // Set of slugs the user (or another tab) has confirmed, derived from
  // `super_form_meta` keys. Single source of truth for tint/dot clearing
  // — the package's PrefillProvider re-derives the pending PARC Array
  // internally from `prefills × confirmedPrefillsSet`.
  const confirmedPrefillsSet = useMemo(() => {
    const set = new Set()
    for (const key of Object.keys(superFormMeta)) {
      if (key.startsWith('confirmed_prefill:')) {
        set.add(key.slice('confirmed_prefill:'.length))
      }
    }
    return set
  }, [superFormMeta])

  const fetchContactRecords = useCallback(async () => {
    if (!urls.contacts) return []
    try {
      const response = await fetch(urls.contacts, {
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken }
      })
      if (response.ok) {
        const data = await response.json()
        return data.map(mapContactRecord)
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Failed to query model records:', error)
    }
    return []
  }, [urls.contacts, csrfToken])

  const queryModelRecords = useCallback(async (model, opts) => {
    if (model === 'Contact' && urls.contacts && opts.clientId) {
      return fetchContactRecords()
    }
    return []
  }, [urls.contacts, fetchContactRecords])

  // Drive the classic server-rendered contact modal (loaded into the layout's
  // #modal turbo-frame) from the React picker. The full form — person
  // typeahead, address autocomplete, role checkboxes — is reused as-is.
  //
  // Resolution is driven by Turbo's own `turbo:submit-end` (which carries a
  // reliable `success` flag and fires after the response), not by watching the
  // modal close: the modal's own controller hides it mid-submit, so
  // `hidden.bs.modal` races ahead of any post-save signal. `pickRecord` selects
  // the saved contact from the refreshed list (the new one for create, the same
  // id for edit). A dismissal with no in-flight submit rejects so the caller
  // (ModelSelectField) adds/edits nothing. The React field owns membership.
  const openContactModal = useCallback((url, pickRecord) => new Promise((resolve, reject) => {
    const modalEl = window.document.getElementById('modal')
    let settled = false
    let submitting = false

    const isContactForm = (event) => (event.target?.action || '').includes('super_form_contacts')

    const cleanup = () => {
      window.document.removeEventListener('turbo:submit-start', onSubmitStart)
      window.document.removeEventListener('turbo:submit-end', onSubmitEnd)
      modalEl?.removeEventListener('hidden.bs.modal', onDismiss)
    }
    const onSubmitStart = (event) => {
      if (isContactForm(event)) submitting = true
    }
    const onSubmitEnd = async (event) => {
      if (settled || !isContactForm(event)) return
      // Validation errors re-render the form in place — keep the modal open.
      if (!event.detail?.success) { submitting = false; return }
      settled = true
      cleanup()
      const records = await fetchContactRecords()
      const record = pickRecord(records)
      if (record) resolve(record)
      else reject(new Error('Saved contact not found'))
    }
    const onDismiss = () => {
      // Ignore the modal close that follows a successful submit; only a genuine
      // cancel (no submit in flight) should reject.
      if (settled || submitting) return
      settled = true
      cleanup()
      reject(new Error('Contact modal dismissed'))
    }

    window.document.addEventListener('turbo:submit-start', onSubmitStart)
    window.document.addEventListener('turbo:submit-end', onSubmitEnd)
    modalEl?.addEventListener('hidden.bs.modal', onDismiss)

    if (modalEl) {
      // Setting the turbo-frame src loads the classic form; its
      // Modals::ConfirmComponent controller shows the modal on connect.
      // Re-setting the same url refetches, so it does not need clearing first.
      loadTurboFrame(modalEl, url)
    }
  }), [fetchContactRecords])

  const onCreateModelRecord = useCallback(async (model) => {
    if (model !== 'Contact' || !urls.newContact) return Promise.reject(new Error('Unsupported model'))
    // The newly created contact is the one absent from the pre-save list.
    const beforeIds = new Set((await fetchContactRecords()).map((r) => String(r.id)))
    return openContactModal(urls.newContact, (records) => records.find((r) => !beforeIds.has(String(r.id))))
  }, [urls.newContact, openContactModal, fetchContactRecords])

  const onEditModelRecord = useCallback((model, record) => {
    if (model !== 'Contact' || !urls.editContactBase || !record?.id) return Promise.reject(new Error('Unsupported model'))
    const url = `${urls.editContactBase}/${record.id}/edit?document_id=${doc?.id}&react=true`
    return openContactModal(url, (records) => records.find((r) => String(r.id) === String(record.id)))
  }, [urls.editContactBase, doc?.id, openContactModal, fetchContactRecords])

  // View-mode header buttons (Download / Back / Re-Open) mirror the classic
  // editor's show page. They're host-rendered so the offline-assessments app
  // (which has no documents list, PDFs, or reopen flow) doesn't grow them.
  const renderActions = useCallback(() => (
    <>
      {readonly && urls.pdf && (
        <IconButton icon="download" variant="borderless" size="sm" href={urls.pdf} target="_blank" tooltip="View as PDF" alt="View as PDF" />
      )}
      {readonly && backLink && (
        <Button variant="borderless" size="sm" href={backLink.url}>
          {backLink.label}
        </Button>
      )}
      {readonly && canReopen && (
        <Button variant="secondary" size="sm" onClick={handleReopen}>
          Re-Open
        </Button>
      )}
    </>
  ), [urls.pdf, readonly, backLink, canReopen, handleReopen])

  const renderBeforeForm = useCallback(() => {
    const banners = []

    // PARC aggregate banner
    banners.push(<PrefillBanner key="parc-banner" />)

    if (externalErrors.length > 0) {
      banners.push(<ExternalErrorsBanner key="external-errors" errors={externalErrors} />)
    }

    if (reopened && hasSignatureConfig) {
      banners.push(
        <div key="reopened-sig" className="p-4 bg-gray-100 rounded-md mb-4">
          <div className="flex items-center">
            <span className="mr-2">&#9888;</span>
            {minorChange ? (
              <span>This document is now re-opened for <strong>minor changes</strong>. The update won't require redoing signatures.</span>
            ) : (
              <span>This document is now <strong>re-opened</strong> for editing. After making the changes, re-sign it again. You can access the previous version in the document version history.</span>
            )}
          </div>
        </div>
      )
    } else if (reopened) {
      banners.push(
        <div key="reopened" className="p-4 bg-gray-100 rounded-md mb-4">
          <div className="flex items-center">
            <span className="mr-2">&#9888;</span>
            <span>This document is now <strong>re-opened</strong> for editing. You can access the previous version in the document version history.</span>
          </div>
        </div>
      )
    }

    return banners.length > 0 ? <>{banners}</> : null
  }, [readonly, reopened, minorChange, hasSignatureConfig, externalErrors])

  return (
    <div className={`react-super-form-container super-form${scannedDocument ? ' has-scan-banner' : ''}`} data-testid="react-document-editor">
      {/* Top level on purpose, not in renderBeforeForm: DocumentEditor drops the renderBeforeForm
          slot when the document is read-only/locked, which would hide the scan link on completed
          and signed documents. Mounting it here keeps it visible in every state. */}
      {scannedDocument && (
        // Similar visual treatment to the scan banner on processors/show.
        <div className="light-purple-bg px-7 py-2" data-testid="scanned-document-banner">
          <div className="d-flex justify-content-center align-items-center">
            <i className="fa fa-exclamation-circle me-2" aria-hidden="true" />
            <div>
              <span>{scannedDocument.message}</span>
              {scannedDocument.warning && <span className="d-block mt-2">{scannedDocument.warning}</span>}
            </div>
            <a href={scannedDocument.url} className="gds--button button--primary mx-10">
              {scannedDocument.linkLabel}
            </a>
          </div>
        </div>
      )}
      <ErrorReporterProvider onError={reportSuperFormError}>
      <FormDataStoreProvider
        initialFormData={initialFormData}
        actionsRef={actionsRef}
        onChange={handleFieldChange}
      >
        <DocumentEditor
          template={template}
          document={doc}
          helpDocument={helpDocument || null}
          summaryHelpDocument={summaryHelpDocument || null}
          onBlur={handleBlur}
          onFieldsRevealed={handleFieldsRevealed}
          onFinish={handleFinish}
          onSaveAndExit={handleSaveAndExit}
          onBack={handleBack}
          title={title}
          clientUrl={urls.client}
          clientSlug={clientSlug}
          defaultPanelOpen={defaultPanelOpen}
          onPanelOpenChange={handlePanelOpenChange}
          renderBeforeForm={renderBeforeForm}
          renderActions={renderActions}
          client={client}
          readonly={readonly}
          errors={errors}
          fieldPermissions={fieldPermissions}
          queryModelRecords={queryModelRecords}
          onCreateModelRecord={urls.newContact ? onCreateModelRecord : undefined}
          onEditModelRecord={urls.editContactBase ? onEditModelRecord : undefined}
          showSummary={template.show_summary}
          initialTab={initialTab}
          showSignature={!!hasSignatureConfig}
          signatures={signatures}
          currentSignerName={currentSignerName}
          onSign={hasSignatureConfig ? handleSign : undefined}
          onReopen={signingActive ? handleReopen : undefined}
          prefills={fieldPrefills}
          confirmedPrefillsSet={confirmedPrefillsSet}
          confirmAllEnabled={confirmAllEnabled}
          clientName={clientName}
          onPrefillConfirmed={handlePrefillConfirmed}
          onPrefillsConfirmed={confirmPrefills}
          showDateCompleted={!!dateCompleted}
          recordedAt={dateCompleted}
          onRecordedAtChange={saveDateCompleted}
          timezone={timezone}
        />
        {showNoChangesNotice && (urls.revert ? (
          <ConfirmDialog
            title="No changes have been made"
            message={`This document is identical to the previous version. If you didn't mean to ${minorChange ? 'make edits to' : 're-open'} this document, you can revert it. If you're still working on it, keep it re-opened.`}
            confirmLabel="Revert"
            cancelLabel="Keep it Re-opened"
            onConfirm={handleRevert}
            onCancel={() => setShowNoChangesNotice(false)}
          />
        ) : (
          <ConfirmDialog
            title="No changes have been made"
            message="This document is identical to the previous version. Make an edit before finishing, or exit to keep it re-opened."
            confirmLabel="OK"
            onConfirm={() => setShowNoChangesNotice(false)}
          />
        ))}
        {revertError && (
          <ConfirmDialog
            title="Unable to revert"
            message={revertError}
            confirmLabel="OK"
            onConfirm={() => setRevertError(null)}
          />
        )}
        {remoteLockNotice && (
          <ConfirmDialog
            title="Document finished elsewhere"
            message="This document was finished by someone else while you had it open. Editing is disabled, and any changes you made since could not be saved. Reload to see the final version."
            confirmLabel="Reload"
            onConfirm={() => window.location.reload()}
          />
        )}
      </FormDataStoreProvider>
      </ErrorReporterProvider>
    </div>
  )
}

export default ReactDocumentEditor
